import type { FormikErrors, FormikValues } from 'formik';
import { setIn } from 'formik';
import { mapValues } from 'lodash';
import { isElement } from 'react-is';
import type { RefinementCtx } from 'zod';
import { z } from 'zod';
import { isObject } from 'src/utils/is-object';

export type FormSchema = z.ZodType<FormikValues, FormikValues>;

// empty values in inputs are ambiguous, because they could mean "empty string" or "null".
// we convert empty strings to null before validation and when passing them into the submit handler.
// therefor an optional number (e.g. <input type="number" />) will always be null when empty -
// this also avoids "expected number, got string" errors.
// this also mimics the behavior of our axios interceptor that converts empty strings to null as well.
// note: it would be best if our input components would do this conversion themselves, but that's a bigger change.
function normalizeFormValues<T>(values: T): T {
  // occasionally a React element is stored inside the form values.
  // this is more of a workaround to re-use it in a different place, but not meant to be ever
  // validated as they will also never be send to the backend.
  if (isElement(values)) return null as T;

  if (values === '') return null as T;
  if (Array.isArray(values)) return values.map(normalizeFormValues) as T;
  if (isObject(values)) return mapValues(values, normalizeFormValues) as T;
  return values;
}

function processFormValues<Schema extends FormSchema>(
  values: z.input<Schema>,
  schema: Schema,
  params?: Partial<z.core.ParseContext<z.core.$ZodIssue>>
) {
  const normalizedValues = normalizeFormValues(values);

  return schema.safeParseAsync(normalizedValues, params);
}

export async function toFormikValidate<Schema extends FormSchema>(
  schema: Schema,
  values: z.input<Schema>,
  params?: Partial<z.core.ParseContext<z.core.$ZodIssue>>
) {
  const result = await processFormValues(values, schema, params);

  if (!result.success) {
    let errors: FormikErrors<any> = {};

    for (let error of result.error.issues) {
      const path = error.path.join('.');
      errors = setIn(errors, path, error.message);
    }

    return errors;
  }
}

export async function validFormValues<Schema extends FormSchema>(
  schema: Schema,
  values: z.input<Schema>,
  params?: Partial<z.core.ParseContext<z.core.$ZodIssue>>
) {
  const result = await processFormValues(values, schema, params);

  if (result.success) return result.data;
  // this should only happen if someone used this function incorrectly
  // (it should only be called when we know the values are valid)
  throw result.error;
}

export function requiredOutput<T>({
  message = 'Please enter a value for {label}.',
}: { message?: string } = {}) {
  return (value: T, ctx: RefinementCtx) => {
    if (value == null) {
      ctx.addIssue({
        code: 'custom',
        message,
        fatal: true, // see https://github.com/colinhacks/zod/issues/2192#issuecomment-1812534617
      });
      return z.NEVER;
    }
    return value;
  };
}

/**
 * Please use this if a `z.discriminatedUnion` cannot be used to create
 * a conditional required check.
 *
 * A good use case for this helper is when you have some condition that
 * is not part of the form values itself (e.g. a field is visible or not).
 */
export function requiredOutputWhen<T>(
  condition: boolean,
  { message = 'Please enter a value for {label}.' }: { message?: string } = {}
) {
  if (!condition) return (value: T) => value;
  return requiredOutput<T>({ message });
}

/**
 * Error message for `z.number().int()`, distinguishing a decimal value from a
 * value that is too large or too small.
 */
function intErrorMessage(
  issue: z.core.$ZodRawIssue<
    | z.core.$ZodIssueInvalidType
    | z.core.$ZodIssueTooBig<'number'>
    | z.core.$ZodIssueTooSmall<'number'>
  >
) {
  if (issue.code === 'too_big') return '{label} is too large.';
  if (issue.code === 'too_small') return '{label} is too small.';
  return '{label} cannot contain decimals.';
}

/**
 * Params for `z.number().int()` with `intErrorMessage` as the default `error`.
 * Use it as `.int(intParams())`, or override `error` (and any other param, e.g. `abort`)
 * as `.int(intParams({ error: customMessage, abort: true }))`.
 */
export function intParams(params?: z.core.$ZodCheckNumberFormatParams) {
  return { error: intErrorMessage, ...params };
}

/**
 * Whenever query parameters are parsed they are only parsed as an array when they appear multiple times.
 * This helper enforces arrays even when there is just one parameter.
 */
export function preprocessAsArray(value: unknown) {
  return value != null && !Array.isArray(value) ? [value] : value;
}

/**
 * Whenever query parameters are parsed a "false" would be parsed as a string
 * and if you use the `coerce` feature of zod as "true". Therefor we manually preprocess it.
 */
export function preprocessAsBoolean(value: unknown) {
  return value === 'false' ? false : value === 'true' ? true : value;
}

/**
 * This is a constant that marks query params as empty (either `null` or `[]`), even though they have a default value
 * that should be used when the query parameter is _really_ missing.
 */
export const ZOD_EXPLICIT_EMPTY = '_null_';

/**
 * This transform allows you to add a default value which can explicitly be set to null or an empty array.
 * It is usually only used for query params where we cannot easily differentiate between "not set at all" and
 * "explicitly set to empty".
 */
export function optionalDefault<T>(defaultValue: T) {
  const isArray = Array.isArray(defaultValue);
  const emptyValue = isArray ? [] : null;
  return (value: unknown) => {
    return value === ZOD_EXPLICIT_EMPTY
      ? emptyValue
      : value === undefined
        ? defaultValue
        : value;
  };
}

export function arrayWithPositionalValidation<
  Item,
  Callback extends (item: Item) => z.ZodType,
>(items: Item[], callback: Callback) {
  return z
    .array(z.custom<z.input<ReturnType<Callback>>>())
    .transform((values, ctx) => {
      const newValue = items.map((item, index) => {
        const value = values[index];
        const valueSchema = callback(item);
        const result = valueSchema.safeParse(value);
        if (!result.success) {
          for (const issue of result.error.issues) {
            ctx.addIssue({
              ...issue,
              path: [index, ...issue.path],
            });
          }
        }
        return result.data;
      });
      return newValue as Array<z.output<ReturnType<Callback>>>;
    });
}
